import React, { useEffect, useState, Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/Button';
import { Card, CardContent } from './ui/Card';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };
  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
    // Log error to monitoring service (Sentry, DataDog, etc)
    // logErrorToService(error, errorInfo)
  }
  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };
  private handleGoHome = () => {
    window.location.href = '/';
  };
  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <div className="min-h-screen bg-white flex items-center justify-center p-4" data-id="element-234">
          <Card className="max-w-2xl w-full" data-id="element-235">
            <CardContent className="p-8" data-id="element-236">
              <div className="flex flex-col items-center text-center" data-id="element-237">
                <div className="h-16 w-16 rounded-full bg-error-light flex items-center justify-center mb-6" data-id="element-238">
                  <AlertTriangle className="h-8 w-8 text-error" data-id="element-239" />
                </div>

                <h1 className="text-2xl font-semibold text-neutral-900 mb-2" data-id="element-240">
                  Terjadi Kesalahan
                </h1>
                <p className="text-neutral-600 mb-6" data-id="element-241">
                  Maaf, terjadi kesalahan yang tidak terduga. Tim kami telah
                  diberitahu dan sedang menangani masalah ini.
                </p>

                {process.env.NODE_ENV === 'development' && this.state.error && <div className="w-full mb-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200 text-left" data-id="element-242">
                    <p className="text-sm font-semibold text-error mb-2" data-id="element-243">
                      Error Details (Development Only):
                    </p>
                    <pre className="text-xs text-neutral-700 overflow-auto max-h-40" data-id="element-244">
                      {this.state.error.toString()}
                      {this.state.errorInfo && <>
                          {'\n\n'}
                          {this.state.errorInfo.componentStack}
                        </>}
                    </pre>
                  </div>}

                <div className="flex gap-3" data-id="element-245">
                  <Button variant="secondary" onClick={this.handleReset} data-id="element-246">
                    <RefreshCw className="w-4 h-4 mr-2" data-id="element-247" />
                    Coba Lagi
                  </Button>
                  <Button variant="primary" onClick={this.handleGoHome} data-id="element-248">
                    <Home className="w-4 h-4 mr-2" data-id="element-249" />
                    Kembali ke Beranda
                  </Button>
                </div>

                <p className="text-xs text-neutral-500 mt-6" data-id="element-250">
                  Jika masalah berlanjut, silakan hubungi support@atios.or.id
                </p>
              </div>
            </CardContent>
          </Card>
        </div>;
    }
    return this.props.children;
  }
}
// Hook untuk error handling di functional components
export function useErrorHandler() {
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);
  return setError;
}